/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Label, Button, Alert, Collapse,
} from 'reactstrap';
import {
  Formik, Field, FieldArray, Form, ErrorMessage,
} from 'formik';
import * as Yup from 'yup';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { isNull } from 'lodash';
import { get, post } from '../../helpers/api_helper';
import * as urls from '../../helpers/url_helper';
import LocationSelector from './locationSelector';

export const pickrouteTypeCodes = [
  // Simulate specialized routes before the general fulfillment pools.
  'Squaretrade Only', 'Dims Class A Fedex', 'Dims Class A', 'Single-Part', 'Multi-Part',
  'Overstock',
  'Tech Orders', 'Scrap Orders',
  'Shark', 'LED Strips', 'Store Pick-Up', 'Priority Orders', 'FedEx', 'ME',
  'Next Day ONLY', 'Batch Route - Early', 'Batch Route - Day', 'Batch Route - EOD',
];

export const pickrouteSimulationTypeCodes = pickrouteTypeCodes.filter(
  (type) => !type.startsWith('Batch Route'),
);

function PickRouteGenerator(props) {
  const { onSuccess, t } = props;

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [advanced, setAdvanced] = useState(false);
  const [startLocationSelect, setStartLocationSelect] = useState(false);
  const [endLocationSelect, setEndLocationSelect] = useState(false);

  const ValidationSchema = Yup.object().shape({
    type: Yup.string().required('Type is required'),
    max_orders: Yup.number().required('Max Orders is required'),
    start_location: Yup.object().shape({
      id: Yup.number(),
    }).nullable(),
    end_location: Yup.object().shape({
      id: Yup.number(),
    }).nullable(),
  });

  const typeOptions = [
    { value: '', label: t('Select Type') },
    { value: 'Single-Part', label: t('Single-Part') },
    { value: 'Dims Class A', label: t('Dims Class A') },
    { value: 'Dims Class A Fedex', label: t('Dims Class A Fedex') },
    { value: 'Multi-Part', label: t('Multi-Part') },
    { value: 'Overstock', label: t('Overstock') },
    { value: 'Tech Orders', label: t('Tech Orders') },
    { value: 'Scrap Orders', label: t('Scrap Orders') },
    { value: 'Shark', label: t('Shark/Ninja') },
    { value: 'LED Strips', label: t('LED Strips') },
    { value: 'Store Pick-Up', label: t('Store Pick-Up') },
    { value: 'Priority Orders', label: t('Priority Orders') },
    { value: 'FedEx', label: t('FedEx Only') },
    { value: 'Squaretrade Only', label: t('Squaretrade Only') },
    { value: 'ME', label: t('M&E') },
    { value: 'Next Day ONLY', label: t('Next Day ONLY') },
    { value: 'Batch Route - Early', label: t('Batch - Early') },
    { value: 'Batch Route - Day', label: t('Batch - Day') },
    { value: 'Batch Route - EOD', label: t('Batch - EOD') },
  ];

  const jsx = (
    <>
      <Alert color="success" className="mt-3" isOpen={(errorMessage === false)}>
        {t('Physical Location created successfully')}
      </Alert>
      <Formik
        enableReinitialize
        initialValues={{
          type: 'Single-Part',
          max_orders: 40,
          start_location: null,
          end_location: null,
        }}
        validationSchema={ValidationSchema}
        onSubmit={async (values) => {
          setLoading(true);

          try {
            await post('/api/warehouse/pick/generate', values);

            toastr.success('Success', 'Pick Route generated successfully');
            onSuccess();
          } catch (error) {
            toastr.error('Error', error.message);
          }

          setLoading(false);
        }}
      >
        {({
          touched, setFieldValue, errors, values,
        }) => (
          <Form className="mt-3" autoComplete="off" id="GeneratePickrouteForm">
            <Row>
              <Col md="6">
                <div className="mb-4">
                  <Label for="type">{t('Type')}</Label>
                  <Field
                    as="select"
                    className={`form-control ${(errors.type && touched.type ? 'is-invalid' : '')}`}
                    id="type"
                    name="type"
                  >
                    {typeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="type"
                    component="div"
                    className="invalid-feedback"
                  />
                </div>
              </Col>

              <Col md="6">
                <div className="mb-4">
                  <Label for="max_orders">{t('Max Orders')}</Label>
                  <Field
                    className={`form-control ${(errors.type && touched.type ? 'is-invalid' : '')}`}
                    id="max_orders"
                    name="max_orders"
                    type="number"
                  />
                  <ErrorMessage
                    name="max_orders"
                    component="div"
                    className="invalid-feedback"
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md="6">
                <div className="mb-4">
                  <Label for="type">
                    <Button
                      type="button"
                      size="sm"
                      color="light"
                      style={{ background: 'none' }}
                      className="btn-link ps-0 pe-1 btn-outline-light fs-6"
                      onClick={() => {
                        setStartLocationSelect(
                          (!startLocationSelect && endLocationSelect === false),
                        );
                      }}
                    >
                      {t('Start Location')}
                    </Button>
                  </Label>
                  <br />
                  {startLocationSelect === false && values.start_location === null && (
                    <small className="text-muted">{t('Optionally select the location where the pick route will end')}</small>
                  )}
                  {startLocationSelect === false && values.start_location !== null && (
                    <>
                      <strong>{values.start_location.label}</strong>
                      <Button color="danger" size="sm" className="btn-icon btn-soft-danger btn-icon-only ms-2 p-1 pt-0 pb-0" onClick={() => { setFieldValue('start_location', null); }}>
                        <span className="btn-icon-wrapper">
                          <i className="mdi mdi-close" />
                        </span>
                      </Button>
                    </>
                  )}
                </div>
              </Col>
              <Col md="6">
                <div className="mb-4">
                  <Label for="type">
                    <Button
                      type="button"
                      size="sm"
                      color="light"
                      style={{ background: 'none' }}
                      className="btn-link ps-0 pe-1 btn-outline-light fs-6"
                      onClick={() => {
                        setEndLocationSelect(
                          (!endLocationSelect && startLocationSelect === false),
                        );
                      }}
                    >
                      {t('End Location')}
                    </Button>
                  </Label>
                  <br />
                  {endLocationSelect === false && values.end_location === null && (
                    <small className="text-muted">{t('Optionally select the location where the pick route will end')}</small>
                  )}
                  {endLocationSelect === false && values.end_location !== null && (
                    <>
                      <strong>{values.end_location.label}</strong>
                      <Button color="danger" size="sm" className="btn-icon btn-soft-danger btn-icon-only ms-2 p-1 pt-0 pb-0" onClick={() => { setFieldValue('end_location', null); }}>
                        <span className="btn-icon-wrapper">
                          <i className="mdi mdi-close" />
                        </span>
                      </Button>
                    </>
                  )}
                </div>
              </Col>
            </Row>

            <div className="mb-3">
              {startLocationSelect === true && (
                <LocationSelector onSelect={(location) => { setFieldValue('start_location', location); setStartLocationSelect(false); }} />
              )}
              {startLocationSelect === false && endLocationSelect === true && (
                <LocationSelector onSelect={(location) => { setFieldValue('end_location', location); setEndLocationSelect(false); }} />
              )}
            </div>

            <Button
              type="button"
              size="sm"
              color="light"
              style={{ background: 'none' }}
              className="btn-link ps-0 pe-1 btn-outline-light fs-6"
              onClick={
                () => {
                  setAdvanced(!advanced);
                }
              }
            >
              {t('Advanced Options')}
              {' '}
              {advanced ? <i className="mdi mdi-chevron-up" /> : <i className="mdi mdi-chevron-down" />}
            </Button>
            <Collapse isOpen={advanced}>
              <p className="mt-3">Your mom is advanced</p>
            </Collapse>
          </Form>
        )}
      </Formik>
    </>
  );

  return jsx;
}

PickRouteGenerator.defaultProps = {
  t: () => { },
  onSuccess: () => { },
};

PickRouteGenerator.propTypes = {
  onSuccess: PropTypes.func,
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(PickRouteGenerator));
